import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import { isSystemForm } from '../lib/systemForms.js';
import { syncBuilderForms } from '../lib/builderSync.js';

interface Named {
  id: string;
  name: string;
  description?: string | null;
  source?: string | null;
}
interface PacketDetail {
  id: string;
  name: string;
  description: string | null;
  packetForms: { form: { id: string; name: string } }[];
}

/**
 * #106 - the shared Bundle editor slide-in, extracted from the Send tab so Form
 * Rules (and anywhere else) can open the same in-context editor. Controlled: the
 * parent sets `open` + `mode` (+ `bundleId` for edit/saveas). The editor fetches
 * its own form catalog and, for edit/saveas, the bundle's current forms — so the
 * caller needs no pre-fetched data. On a successful save it calls `onSaved` (the
 * parent reloads its bundle list) then `onClose`. Uses the global `pb-*` styles.
 */
export function BundleEditor({
  open,
  mode,
  bundleId,
  manage,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: 'new' | 'edit' | 'saveas';
  bundleId: string | null;
  manage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const forms = useFetch<Named[]>(() => api.get('/forms'), []);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [formIds, setFormIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Load state when the editor opens (or the target bundle changes). For edit /
  // saveas it fetches the bundle so callers don't need its forms pre-loaded.
  useEffect(() => {
    if (!open) return;
    setError('');
    setSearch('');
    if (mode !== 'new' && bundleId) {
      let live = true;
      api
        .get<PacketDetail>(`/packets/${bundleId}`)
        .then((p) => {
          if (!live) return;
          setEditId(mode === 'edit' ? p.id : null);
          setName(mode === 'edit' ? p.name : `${p.name} (copy)`);
          setDesc(p.description ?? '');
          setFormIds(p.packetForms.map((pf) => pf.form.id));
        })
        .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
      return () => {
        live = false;
      };
    }
    setEditId(null);
    setName('');
    setDesc('');
    setFormIds([]);
    return undefined;
  }, [open, mode, bundleId]);

  const sendableForms = (forms.data ?? []).filter(
    (f) => isSystemForm(f.name) || f.source === 'builder',
  );
  const q = search.trim().toLowerCase();
  const results = sendableForms.filter((f) => !q || f.name.toLowerCase().includes(q));
  const list = formIds
    .map((id) => sendableForms.find((f) => f.id === id))
    .filter(Boolean) as Named[];

  async function save() {
    if (!name.trim()) {
      setError('Bundle name is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body = { name: name.trim(), description: desc.trim() || null, formIds };
      if (editId) await api.patch(`/packets/${editId}`, body);
      else await api.post('/packets', body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // fbs#156: no "Sync from Form Builder" button. Opening the editor pulls the practice's
  // Form Builder forms in on its own (admins / managers; throttled in lib/builderSync).
  useEffect(() => {
    if (!open || !manage) return;
    let live = true;
    setSyncing(true);
    void syncBuilderForms()
      .then((r) => {
        if (live && r && r.imported) forms.reload();
      })
      .finally(() => live && setSyncing(false));
    return () => {
      live = false;
    };
  }, [open, manage]);

  return (
    <>
      <div className={`pb-overlay${open ? ' open' : ''}`} onClick={onClose} />
      <div
        className={`pb-panel${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Bundle Builder"
      >
        <div className="pb-head">
          <h2>
            <span>{editId ? 'Edit Bundle' : 'New Bundle'}</span>
          </h2>
          <button className="close-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pb-body">
          <div>
            <div className="field-group" style={{ marginBottom: 12 }}>
              <label>
                Bundle Name <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. SUD Re-entry Bundle"
              />
            </div>
            <div className="field-group">
              <label>Description</label>
              <input
                type="text"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Brief description of this bundle"
              />
            </div>
          </div>
          <div>
            <div
              className="section-label"
              style={{
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>Add Forms</span>
              {manage && (
                <span className="pb-sync-note" aria-live="polite">
                  {syncing
                    ? 'Getting the latest Form Builder forms…'
                    : 'Form Builder forms sync automatically'}
                </span>
              )}
            </div>
            <div className="pb-form-search-wrap" style={{ marginBottom: 8 }}>
              <span className="pb-search-icon">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search forms..."
                autoComplete="off"
              />
            </div>
            <div className="pb-results">
              {results.length === 0 ? (
                <div className="form-search-empty">No forms match.</div>
              ) : (
                results.map((f) => (
                  <label className="pb-result-item" key={f.id}>
                    <input
                      type="checkbox"
                      checked={formIds.includes(f.id)}
                      onChange={() =>
                        setFormIds((ids) =>
                          ids.includes(f.id) ? ids.filter((x) => x !== f.id) : [...ids, f.id],
                        )
                      }
                    />
                    <span className="pb-result-name">{f.name}</span>
                    {f.description && <span className="pb-result-meta">{f.description}</span>}
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div className="section-label" style={{ marginBottom: 0 }}>
                Forms in Bundle{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                  ({formIds.length})
                </span>
              </div>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted-2)' }}>Drag to reorder</span>
            </div>
            <div className="pb-form-list">
              {list.length === 0 ? (
                <div className="pb-empty-list">No forms added yet.</div>
              ) : (
                list.map((f, i) => (
                  <div
                    key={f.id}
                    className={`pb-form-row${dragIdx === i ? ' dragging' : ''}${dragOver === i && dragIdx !== i ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(i);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIdx === null || dragIdx === i) return;
                      setFormIds((ids) => {
                        const next = [...ids];
                        const [moved] = next.splice(dragIdx, 1);
                        next.splice(i, 0, moved!);
                        return next;
                      });
                      setDragIdx(null);
                      setDragOver(null);
                    }}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setDragOver(null);
                    }}
                  >
                    <span className="pb-drag-handle">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="none"
                      >
                        <circle cx="8" cy="6" r="1.6" />
                        <circle cx="16" cy="6" r="1.6" />
                        <circle cx="8" cy="12" r="1.6" />
                        <circle cx="16" cy="12" r="1.6" />
                        <circle cx="8" cy="18" r="1.6" />
                        <circle cx="16" cy="18" r="1.6" />
                      </svg>
                    </span>
                    <span className="pb-row-name">{f.name}</span>
                    <button
                      className="pb-row-remove"
                      title="Remove form"
                      onClick={() => setFormIds((ids) => ids.filter((x) => x !== f.id))}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          {error && <span className="badge urgent">{error}</span>}
        </div>
        <div className="pb-foot">
          <button className="fh-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="fh-btn primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save Bundle'}
          </button>
        </div>
      </div>
    </>
  );
}
